import React, { useState } from 'react';

// Charter member badge, from the Claude Design handoff (design_handoff_charter_badge).
// A struck-gold disc with a debossed star; the words live in the tooltip, not
// on the disc. Pure CSS, no image, no SVG. One --size drives every variant:
// the star is 0.39 × size, the inner face a constant 72%. At 24px and below the
// inner deboss muddies, so it's dropped.
//
// Permanent by design: charter is a fact about when someone joined, so this
// reads charterSince and nothing about the subscription's current state.

const GRADIENT = 'linear-gradient(155deg, #f6e2a4 0%, #d9b45f 42%, #a8813a 74%, #e8cd86 100%)';

export const charterSinceLabel = (since?: string | null) => {
  if (!since) return '';
  const d = new Date(since);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const CharterBadge: React.FC<{ size?: number; since?: string | null; tooltip?: boolean; className?: string }> = ({ size = 36, since, tooltip = true, className }) => {
  const [open, setOpen] = useState(false);
  const when = charterSinceLabel(since);
  const label = `Charter member${when ? ` since ${when}` : ''}`;
  const small = size <= 24;
  return (
    <span
      className={`relative inline-flex ${className || ''}`}
      style={{ flex: 'none' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={tooltip ? 0 : -1}
      aria-label="Charter member"
      title={tooltip ? undefined : label}
    >
      <span
        className="inline-flex items-center justify-center"
        style={{
          width: size, height: size, borderRadius: 999,
          background: GRADIENT,
          boxShadow: '0 1px 2px rgba(0,0,0,.6), inset 0 1px 1px rgba(255,255,255,.55), inset 0 -1px 2px rgba(80,55,15,.5)',
        }}
      >
        <span
          className="inline-flex items-center justify-center"
          style={{
            width: '72%', height: '72%', borderRadius: 999,
            boxShadow: small ? 'none' : 'inset 0 1px 1.5px rgba(90,62,18,.55), inset 0 -1px 1px rgba(255,255,255,.35)',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: size * 0.39, lineHeight: 1, color: '#7a5a1e', textShadow: '0 1px 0 rgba(255,255,255,.45)' }}>&#9733;</span>
        </span>
      </span>
      {tooltip && open && (
        <span
          role="tooltip"
          className="absolute"
          style={{
            top: size + 8, left: '50%', transform: 'translateX(-50%)', zIndex: 50, whiteSpace: 'nowrap',
            display: 'flex', flexDirection: 'column', gap: 4,
            padding: '10px 14px', borderRadius: 10, background: '#191713', border: '1px solid #26221b', boxShadow: '0 8px 24px rgba(0,0,0,.55)',
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: '#f2ede3' }}>Charter member</span>
          {when && <span style={{ fontSize: 11, fontFamily: 'var(--hj-font-mono), ui-monospace, monospace', color: '#9d9689' }}>Since {when}</span>}
        </span>
      )}
    </span>
  );
};

// The Account page row: gives the status a home that doesn't depend on a hover.
export type CharterRenewalInfo = {
  endsAt: string;
  renewsAtCents: number;
  optOut: boolean;
  card: { brand: string; last4: string } | null;
};

// The renewal line says what the charter page promised: the term, then $99 a
// year. Only rendered while the member is still on a prepaid term; once they've
// renewed they're a yearly subscriber and the membership card covers it.
const renewalLine = (r: CharterRenewalInfo) => {
  const d = new Date(r.endsAt);
  const when = isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const price = `$${Math.round(r.renewsAtCents / 100)}`;
  if (r.optOut) return `Your term runs through ${when}. It won't renew after that.`;
  const card = r.card ? ` on your ${r.card.brand} ending in ${r.card.last4}` : '';
  return `Your term runs through ${when}. It renews at ${price} a year after that${card}. We'll email you 30 days before.`;
};

export const CharterMemberRow: React.FC<{ since?: string | null; renewal?: CharterRenewalInfo | null }> = ({ renewal }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14,
      border: '1px solid #26221b', background: 'linear-gradient(180deg, #131110, #0d0c0b)',
    }}
  >
    <CharterBadge size={44} tooltip={false} />
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 15, fontWeight: 700, color: '#f2ede3' }}>Charter member</span>
      <span style={{ fontSize: 13, color: '#9d9689' }}>One of the first to join Hey Joe Plus</span>
      {renewal && <span style={{ fontSize: 13, color: '#f2ede3', marginTop: 4 }}>{renewalLine(renewal)}</span>}
    </div>
  </div>
);
